import React, { useEffect, useState, useCallback } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import { DCard, BtnGold } from '../components/MarketplaceAtoms.js';
import { useProfile } from '../hooks/useProfile.js';
import { profileGroovesBalance } from '../constants/groovesEconomy.js';
import {
  fetchGroovesCheckoutSessionStatus,
  fulfillGroovesCheckoutSession,
} from '../../lib/groovesStripeService.js';

const POLL_MS = 2500;
const POLL_MAX = 12;

export default function PageGroovesCheckout({ outcome, sessionId, onBack, onNavigate, user }) {
  const isSuccess = outcome === 'success';
  const isCancel = outcome === 'cancel';
  const { profile, loading: profileLoading, refetch } = useProfile(user?.id);
  const [sessionRow, setSessionRow] = useState(null);
  const [sessionErr, setSessionErr] = useState('');
  const [fulfillErr, setFulfillErr] = useState('');
  const [fulfilling, setFulfilling] = useState(false);
  const [pollDone, setPollDone] = useState(false);

  const loadSession = useCallback(async () => {
    if (!isSuccess || !sessionId || !user?.id) return null;
    const { data, error } = await fetchGroovesCheckoutSessionStatus(sessionId);
    if (error) {
      setSessionErr(error);
      return null;
    }
    setSessionRow(data);
    if (data?.fulfilled_at) setPollDone(true);
    return data;
  }, [isSuccess, sessionId, user?.id]);

  const tryFulfill = useCallback(async () => {
    if (!isSuccess || !sessionId || !user?.id) return false;
    setFulfilling(true);
    setFulfillErr('');
    const { ok, fulfilled, error } = await fulfillGroovesCheckoutSession(sessionId);
    setFulfilling(false);
    if (error && !/procesando|espera unos segundos/i.test(error)) {
      setFulfillErr(error);
    }
    if (ok && fulfilled) {
      await loadSession();
      await refetch();
      setPollDone(true);
      return true;
    }
    return false;
  }, [isSuccess, sessionId, user?.id, loadSession, refetch]);

  useEffect(() => {
    if (!isSuccess || !user?.id || !sessionId) return undefined;
    let cancelled = false;

    (async () => {
      const row = await loadSession();
      if (cancelled) return;
      if (!row?.fulfilled_at) await tryFulfill();
    })();

    if (pollDone) return () => { cancelled = true; };

    let n = 0;
    const t = window.setInterval(async () => {
      n += 1;
      const row = await loadSession();
      if (!row?.fulfilled_at) await tryFulfill();
      else await refetch();
      if (n >= POLL_MAX) {
        setPollDone(true);
        window.clearInterval(t);
      }
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [isSuccess, user?.id, sessionId, loadSession, tryFulfill, refetch, pollDone]);

  useEffect(() => {
    if (sessionRow?.fulfilled_at) setPollDone(true);
  }, [sessionRow?.fulfilled_at]);

  const groovesTotal = profile ? profileGroovesBalance(profile) : 0;
  const fulfilled = !!sessionRow?.fulfilled_at;
  const pendingWebhook = isSuccess && user?.id && sessionId && !fulfilled && pollDone;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#fff' }}>
      <WebTopBar
        user={user}
        onBack={onBack}
        onNavigate={onNavigate}
        title={isSuccess ? 'Pago completado' : 'Pago cancelado'}
      />
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '20px 16px 48px' }}>
        <DCard style={{ padding: 20, textAlign: 'center' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isSuccess ? 'rgba(29,158,117,0.15)' : 'rgba(239,159,39,0.12)',
              border: `0.5px solid ${isSuccess ? 'rgba(29,158,117,0.4)' : 'rgba(239,159,39,0.35)'}`,
            }}
          >
            <Icon name={isSuccess ? 'check' : 'x'} size={26} color={isSuccess ? C.green : C.warn} />
          </div>

          {isSuccess ? (
            <>
              <h1 style={{ fontSize: 18, fontWeight: 600, color: '#fff', margin: '0 0 10px' }}>
                ¡Gracias por tu compra!
              </h1>
              {!user ? (
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, margin: '0 0 16px' }}>
                  Inicia sesión con la misma cuenta con la que pagaste para ver tus Grooves actualizados.
                </p>
              ) : (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, margin: '0 0 16px' }}>
                {fulfilled
                  ? 'Tus Grooves ya están en tu cuenta. Verás una notificación en la campana (arriba a la derecha) confirmando la compra.'
                  : 'Stripe ha confirmado el pago. Estamos abonando tus Grooves; suele tardar unos segundos.'}
              </p>
              )}
              {sessionRow?.grooves_amount ? (
                <p style={{ fontSize: 14, fontWeight: 600, color: C.gold, margin: '0 0 8px' }}>
                  +{sessionRow.grooves_amount} Grooves
                  {sessionRow.pack_code ? ` · ${sessionRow.pack_code}` : ''}
                </p>
              ) : null}
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: '0 0 18px' }}>
                Saldo actual: {profileLoading ? '…' : groovesTotal} Grooves
              </p>
              {pendingWebhook ? (
                <>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.45, marginBottom: 10 }}>
                    {fulfilling
                      ? 'Confirmando tu pago con Stripe…'
                      : 'Si tarda más de un minuto, pulsa el botón para abonar los Grooves de nuevo.'}
                  </p>
                  <BtnGold
                    type="button"
                    disabled={fulfilling}
                    onClick={() => tryFulfill()}
                    style={{
                      width: '100%',
                      marginBottom: 12,
                      opacity: fulfilling ? 0.7 : 1,
                      background: 'transparent',
                      border: `0.5px solid ${C.gold}`,
                      color: C.gold,
                    }}
                  >
                    {fulfilling ? 'Confirmando…' : 'Abonar mis Grooves ahora'}
                  </BtnGold>
                </>
              ) : null}
              {fulfillErr ? (
                <p style={{ fontSize: 11, color: 'rgba(239,159,39,0.9)', marginBottom: 12 }}>{fulfillErr}</p>
              ) : null}
              {sessionErr ? (
                <p style={{ fontSize: 11, color: 'rgba(239,159,39,0.9)', marginBottom: 12 }}>{sessionErr}</p>
              ) : null}
            </>
          ) : (
            <>
              <h1 style={{ fontSize: 18, fontWeight: 600, color: '#fff', margin: '0 0 10px' }}>
                Pago cancelado
              </h1>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, margin: '0 0 18px' }}>
                No se ha cobrado nada. Puedes volver a intentarlo cuando quieras desde tu perfil o Ajustes.
              </p>
            </>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <BtnGold type="button" onClick={() => onNavigate('perfil')} style={{ width: '100%' }}>
              Ver mis Grooves
            </BtnGold>
            {isCancel ? (
              <BtnGold
                type="button"
                onClick={() => onNavigate('ajustes', { initialSub: 'cuenta' })}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: `0.5px solid ${C.gold}`,
                  color: C.gold,
                }}
              >
                Comprar otro paquete
              </BtnGold>
            ) : null}
            <button
              type="button"
              onClick={() => onNavigate('marketplace')}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.15)',
                background: 'transparent',
                color: 'rgba(255,255,255,0.6)',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Volver al mercado
            </button>
          </div>
        </DCard>
      </div>
    </div>
  );
}
